import React, { useEffect, useMemo, useRef } from 'react';
import { Box, Card, CardContent, CardHeader, Stack, Typography, useTheme } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { formatCurrency, formatDate, getOutstandingDays } from '@/utils/constants';
import { Draggable } from '@hello-pangea/dnd';
import { formatDistanceToNow, isAfter } from 'date-fns';
import { useGetUserDetailsQuery } from '@/features/user/userApi';
import BrokerAvatar from '@/components/BrokerAvatar';
import { safelyParseDate } from '@/utils/date';
import { usePreviewMode } from '@/hooks/usePreviewMode';
import { useSelector } from 'react-redux';
import { getDealFromCache } from '@/features/deals/dealsApi';
import { getClientFromCache } from '@/features/clients/clientsApi';
import { useKanbanContext } from '@/context/kanbanContext';
import { usePipelineContext } from '@/context/pipelineContext';
import { useFeatureFlagContext } from '@/context/featureFlagContext';
import DebtorAutomationToggle from '@/components/DebtorAutomationToggle';
import { paths } from '@/app/routes';
import FolioLink from '@/components/FolioLink';
import { handlePlainClick, isModifiedClick } from '@/utils/navigation';

const today = new Date();
const DRAG_CLICK_THRESHOLD = 5;

export const KanbanCard = ({ hit, index }) => {
	const theme = useTheme();

	const { shiftDeal, dealStages, isProspect } = useKanbanContext();

	const navigate = useNavigate();
	const { openPreview, closePreview, previewId } = usePreviewMode();
	const pointerStartRef = useRef(null);

	const dealId = hit.dealId;
	const prospectId = hit.clientId || hit.id;
	const recordId = isProspect ? prospectId : dealId;
	const draggableId = String(recordId);

	const cachedDeal = useSelector((state) =>
		isProspect
			? prospectId
				? getClientFromCache(state, prospectId)
				: null
			: dealId
				? getDealFromCache(state, dealId)
				: null,
	);

	const cachedStage = isProspect ? cachedDeal?.stage : cachedDeal?.dealStage;
	const hitStage = isProspect ? hit.stage : hit.dealStage;

	const deal = useMemo(() => ({ ...hit, ...(cachedDeal ?? {}) }), [hit, cachedDeal]);

	const { stages } = usePipelineContext(deal.pipeline);
	const { getFeatureFlags } = useFeatureFlagContext();
	const automatedDebtors = Boolean(getFeatureFlags('automatedDebtors')?.active);

	const isClaim = deal.pipeline === process.env.REACT_APP_PIPELINE_CLAIMS;
	const isDebtor = deal.pipeline === process.env.REACT_APP_PIPELINE_DEBTORS;

	useEffect(() => {
		if (!cachedStage) return;
		if (hitStage === cachedStage) return;

		const updatedDeal = isProspect
			? { ...hit, stage: cachedStage }
			: { ...hit, dealStage: cachedStage };

		shiftDeal(hitStage, cachedStage, index, 0, updatedDeal);
	}, [isProspect, cachedStage, hitStage, index, shiftDeal, hit]);

	const { data: owner, ...ownerQuery } = useGetUserDetailsQuery(
		{ userId: deal?.ownerDocId },
		{ skip: !deal?.ownerDocId },
	);

	const ownerName = !ownerQuery?.isSuccess ? '' : (owner?.name ?? owner?.email ?? 'None');

	const due = useMemo(() => {
		const timestamp = deal?.earliestTask?.timestamp || deal?.nextTaskDate;
		return safelyParseDate(timestamp);
	}, [deal?.earliestTask, deal?.nextTaskDate]);

	const overdue = useMemo(() => (due ? isAfter(today, due) : false), [due]);

	const nextTaskString = useMemo(() => {
		if (!due) {
			return 'No upcoming tasks';
		}
		const dueTime = formatDistanceToNow(due);
		if (overdue) {
			return `Due ${dueTime} ago`;
		} else {
			return `Due in ${dueTime}`;
		}
	}, [due, overdue]);

	const formattedAmount = useMemo(() => {
		const amount = isProspect ? deal.prospectAmount : deal.dealAmount;
		if (!amount) return null;
		return formatCurrency({ num: amount, showDecimal: true });
	}, [deal.dealAmount, deal.prospectAmount, isProspect]);

	const transactions = (deal?.policies ?? []).map((p) => p?.transaction).filter(Boolean);

	const balance = transactions.reduce((acc, trans) => {
		if (!trans?.Id) return acc;
		return acc + (trans.Balance ?? 0);
	}, 0);

	const outstandingDays = useMemo(() => {
		const date = deal?.effectiveDate ? safelyParseDate(deal.effectiveDate) : null;
		const currentStage = stages?.find((s) => s.stageId === deal.dealStage);

		if (!isDebtor || currentStage?.completed || currentStage?.closed) return '';
		if (!date || currentStage?.order !== 0) return '-';
		const days = getOutstandingDays(date);
		if (days === 1) return '1 day';
		return `${days} days`;
	}, [isDebtor, deal.effectiveDate, deal.dealStage, stages]);

	const formattedCloseDate = useMemo(() => {
		const date = deal?.estimatedCloseDate ? safelyParseDate(deal.estimatedCloseDate) : null;
		if (!date) return null;
		return formatDate(date, false);
	}, [deal.estimatedCloseDate]);

	const title = isProspect ? deal.name : deal.dealName;
	const subheader = isProspect ? (deal.contactName ?? null) : (deal.clientName ?? 'Not found');
	const recordPath = isProspect ? `${paths.prospects}/${prospectId}` : `${dealId}`;

	const handleTitleClick = (event) => {
		if (isModifiedClick(event)) return;
		event.stopPropagation();
		event.preventDefault();
		navigate(recordPath);
	};

	//Determines if the card is being clicked or dragged
	const handleCardClick = (event) => {
		if (!pointerStartRef.current) return;
		const dx = Math.abs(event.clientX - pointerStartRef.current.x);
		const dy = Math.abs(event.clientY - pointerStartRef.current.y);

		if (dx > DRAG_CLICK_THRESHOLD || dy > DRAG_CLICK_THRESHOLD) {
			return;
		}

		handlePlainClick(event, () => {
			const hasPreview = previewId;
			if (hasPreview) closePreview();
			setTimeout(() => openPreview(recordId), hasPreview ? 200 : 1);
		});
	};

	const handlePointerDown = (event) => {
		pointerStartRef.current = { x: event.clientX, y: event.clientY };
	};

	const options = [
		...(deal.claimId
			? [
					<Typography
						key={'claim-id'}
						noWrap
						variant='avatar_subtitle'
						sx={{ fontSize: '0.75rem' }}
					>
						<span style={{ fontWeight: 600 }}>{'Claim: '}</span>
						{deal.claimId}
					</Typography>,
				]
			: []),

		...(isDebtor
			? [
					<Typography
						key='balance'
						variant='avatar_subtitle'
						sx={{ fontSize: '0.75rem' }}
					>
						<span style={{ fontWeight: 600 }}>{'Balance: '}</span>
						{formatCurrency({
							num: balance,
							showDecimal: true,
						})}
					</Typography>,
				]
			: []),
		...(isDebtor && automatedDebtors
			? [
					<Stack
						key='automation'
						direction='row'
						spacing={0.5}
						alignItems='center'
						onClick={(event) => {
							event.stopPropagation();
							if (event.target.type !== 'checkbox') {
								event.preventDefault();
							}
						}}
						onMouseDown={(event) => {
							event.stopPropagation();
							event.preventDefault();
						}}
					>
						<Typography
							variant='avatar_subtitle'
							sx={{ fontSize: '0.75rem', fontWeight: 600 }}
						>
							Reminders:
						</Typography>
						<DebtorAutomationToggle
							dealId={dealId}
							automationEnabled={deal.automationEnabled}
							disabled={deal.policiesLoading}
						/>
					</Stack>,
				]
			: []),
		...(!isClaim && !isDebtor && formattedAmount
			? [
					<Typography key='amount' variant='avatar_subtitle' sx={{ fontSize: '0.75rem' }}>
						<span style={{ fontWeight: 600 }}>{'Amount: '}</span>
						{formattedAmount}
					</Typography>,
				]
			: []),

		...(!isProspect && !isClaim && !isDebtor && formattedCloseDate
			? [
					<Typography
						key={'est-close-date'}
						variant='avatar_subtitle'
						sx={{ fontSize: '0.75rem' }}
					>
						<span style={{ fontWeight: 600 }}>{'Est. close: '}</span>
						{formattedCloseDate}
					</Typography>,
				]
			: []),
		...(outstandingDays
			? [
					<Typography
						key={'effective-date'}
						variant='avatar_subtitle'
						sx={{ fontSize: '0.75rem' }}
					>
						<span style={{ fontWeight: 600 }}>{'Outstanding: '}</span>
						{outstandingDays}
					</Typography>,
				]
			: []),
	];

	return (
		<Draggable draggableId={draggableId} index={index} key={draggableId}>
			{(provided, snapshot) => (
				<Box
					mb={0.75}
					{...provided.draggableProps}
					{...provided.dragHandleProps}
					ref={provided.innerRef}
				>
					<FolioLink
						to={recordPath}
						stopPropagation
						sx={{ display: 'block', color: 'inherit' }}
					>
						<Card
							{...(isProspect
								? { 'data-client-preview-trigger': true }
								: { 'data-deal-preview-trigger': true })}
							onPointerDown={handlePointerDown}
							onClick={handleCardClick}
							{...(theme.palette.mode === 'dark' && { elevation: 0 })}
							sx={{
								opacity: snapshot.isDragging ? 0.9 : 1,
								transform: snapshot.isDragging ? 'rotate(-2deg)' : '',
								cursor: snapshot.isDragging ? 'grabbing' : 'pointer',
								position: 'relative',
								'&:hover': {
									backgroundColor: 'action.hover',
								},
								'& .MuiCardContent-root:last-child': {
									pb: 2,
								},
								...(theme.palette.mode === 'dark' && {
									border: `1px solid ${theme.palette.divider}`,
								}),
							}}
							elevation={snapshot.isDragging ? 3 : 1}
						>
							<CardHeader
								title={title}
								subheader={subheader}
								onClick={handleTitleClick}
								sx={{
									px: 1.2,
									pb: 1,
									color: theme.palette.primary.main,
									'& .MuiCardHeader-content .MuiTypography-root': {
										fontSize: '0.85rem',
										fontWeight: 600,
									},
									'& .MuiCardHeader-content .MuiCardHeader-subheader': {
										fontSize: '0.75rem',
									},
									'& .MuiCardHeader-title': {
										cursor: 'pointer',
										'&:hover': {
											textDecoration: 'underline',
										},
									},
								}}
							/>
							<CardContent sx={{ pt: 0, px: 1.2, height: '100%' }}>
								<Stack
									spacing={2}
									sx={{ justifyContent: 'space-between', height: '100%' }}
								>
									{options.length > 0 && (
										<Stack spacing={0.5}>
											{options.map((option) => option)}
										</Stack>
									)}
									<Typography
										variant='task_updated'
										sx={{
											fontSize: '0.7rem',
											...(due &&
												overdue && {
													color: 'error.main',
													fontWeight: 600,
												}),
										}}
									>
										<span style={{ fontWeight: 600 }}>Next task:</span>{' '}
										{due ? nextTaskString : 'None'}
									</Typography>

									{owner && (
										<Stack
											direction='row'
											spacing={0.5}
											sx={{ alignItems: 'center' }}
										>
											<BrokerAvatar broker={owner} />
											<Typography
												sx={{
													fontSize: '0.75rem',
													color: 'text.secondary',
												}}
											>
												{ownerName}
											</Typography>
										</Stack>
									)}
								</Stack>
							</CardContent>
						</Card>
					</FolioLink>
				</Box>
			)}
		</Draggable>
	);
};
